export const DEEP_LINK_MESSAGE_MIN_LENGTH = 2;
export const DEEP_LINK_MESSAGE_MAX_LENGTH = 300;

export type DeepLinkMessageRejectReason = 'empty' | 'too_short' | 'too_long';

type SanitizeDeepLinkMessageResult =
    | { ok: true; message: string }
    | { ok: false; reason: DeepLinkMessageRejectReason; length: number };

// Tabs and newlines are control characters, but they carry a word boundary, so they become a space
// before the control-character strip rather than vanishing and gluing two words together.
const LINE_BREAK_REGEX = /[\t\n\r]/g;

// Control characters, zero-width/invisible characters, and bidi overrides. None of these can be
// typed into the chat composer, so a deep link carrying them is either a mangled email template or
// someone dressing a message up to read as something else.
const INVISIBLE_CHAR_REGEX =
    /[\u0000-\u001F\u007F-\u009F\u200B-\u200D\u2060\uFEFF\u00AD\u200E\u200F\u202A-\u202E\u2066-\u2069]/g; // eslint-disable-line no-control-regex

// A tag starts with `<` followed by a letter or `/`, and `!` or `?` opens a comment, a doctype, or a
// processing instruction. That is the same rule the HTML tokenizer uses, so a bare comparison like
// "revenue < 50k > expenses" is left alone.
const TAG_REGEX = /<(?:\/?[a-zA-Z]|!|\?)[^>]*>/g;
const WHITESPACE_RUN_REGEX = /\s+/g;

/**
 * Cleans and bounds the `?message=` value of a Help Agent deep link before it is prefilled into the
 * chat.
 *
 * This is hygiene, not security. The member can type anything they like into the chat composer, so
 * the sanitizer is not a trust boundary: it exists so a link built by an email template, a partner,
 * or a hand-edited URL cannot open the agent on a message that reads as something other than what it
 * is (invisible characters, bidi overrides, markup) or on a wall of text the agent has to wade
 * through.
 *
 * An over-length message is rejected, never truncated. Truncating would hand the agent half a
 * question, which is worse than an empty composer: the member sees a sentence they did not write,
 * cut off mid-thought, and the agent answers the fragment. Rejecting still opens the widget, so the
 * member simply starts by typing.
 *
 * The bound is measured in Unicode code points after cleaning, so a single emoji counts as 1, though
 * a multi-code-point sequence (a skin-tone or ZWJ emoji) still counts once per code point. Grapheme
 * segmentation (Intl.Segmenter) is deliberately not used: the browserslist target (`>0.2%, not
 * dead`) includes browsers without it, and a 300 code point hygiene bound does not need that
 * precision.
 *
 * @param raw - The raw `message` query param, or null/undefined when the link did not carry one.
 * @returns The cleaned message, or the reason it was rejected plus the cleaned length (never the
 * string itself, which is member content and must not reach analytics).
 */
export function sanitizeDeepLinkMessage(raw: string | null | undefined): SanitizeDeepLinkMessageResult {
    if (raw === null || raw === undefined) {
        return { ok: false, reason: 'empty', length: 0 };
    }

    // Bound the work before anything parses the string. A URL can carry hundreds of KB, and both the
    // HTML parse and the regex passes below are linear in the input: the reviewer measured 4.8s on a
    // 360KB `&lt;a ` repeat. Nothing this long can survive the 300 check at the end anyway, so it is
    // rejected on the raw length and never cleaned. That final 300 check still applies to everything
    // that gets past here. This guard is intentionally measured in UTF-16 code units: it is a
    // pre-parse work bound, not the user-facing length, so code units are the right unit here.
    if (raw.length > DEEP_LINK_MESSAGE_MAX_LENGTH * 8) {
        return { ok: false, reason: 'too_long', length: raw.length };
    }

    // Decode first, on the raw string. DOMParser resolves entities and drops real tags and comments
    // without ever executing or attaching anything (it builds a detached document). This has to run
    // ahead of the strips below rather than after them: `he&#x202E;llo`, `a&#8203;b`, and
    // `&#xFF1C;b&#xFF1E;` are plain ASCII until they are decoded, so a strip that runs first finds
    // nothing to remove and the invisible or the disguised tag survives into the cleaned message.
    const decoded = new DOMParser().parseFromString(raw, 'text/html').body.textContent ?? '';

    // Then NFKC, so fullwidth and compatibility forms the decode just produced fold into their ASCII
    // equivalents, which is what makes the tag strip below see a disguised tag as a tag. Line breaks
    // become spaces before the invisible strip so they carry their word boundary instead of vanishing
    // and gluing two words together.
    const normalized = decoded.normalize('NFKC').replace(LINE_BREAK_REGEX, ' ').replace(INVISIBLE_CHAR_REGEX, '');

    // Last, the tag strip: markup that only became literal once the entities were decoded, plus any
    // fullwidth brackets NFKC has just folded. The DOMParser pass above could not have seen either of
    // those as markup.
    const withoutMarkup = normalized.replace(TAG_REGEX, '');

    const message = withoutMarkup.replace(WHITESPACE_RUN_REGEX, ' ').trim();
    // Array.from iterates the string by code point, not by UTF-16 code unit, so a single emoji
    // counts as 1 here. Destructured because `Array.from(message).length` trips prefer-destructuring.
    const { length } = Array.from(message);

    if (length === 0) {
        return { ok: false, reason: 'empty', length };
    }
    if (length < DEEP_LINK_MESSAGE_MIN_LENGTH) {
        return { ok: false, reason: 'too_short', length };
    }
    if (length > DEEP_LINK_MESSAGE_MAX_LENGTH) {
        return { ok: false, reason: 'too_long', length };
    }

    return { ok: true, message };
}
